import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";
import { getMetricsData } from "../../Common/ProductView.common";
import {
  ProductTelemetryTabMetricsCardProps
} from "../../Common/ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import { DailyChartWrapper } from "./DailyChartWrapper";
import { DEFAULT_AGGREGATION_TYPE } from "./ProductTelemetryTab.constants";

export const ProductTelemetryTabMetricsCard = ({
  data,
  error,
  header,
  isError,
  isLoading,
  isSuccess,
  isTenantMetrics,
  key,
  kustoLink,
  selectedAggregation = DEFAULT_AGGREGATION_TYPE
}: ProductTelemetryTabMetricsCardProps) => {
  const [width, setWidth] = useState(500);
  const widthCalc = useRef<HTMLDivElement>(null);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const displayAPIsException = isError && <DisplayAPIsException error={error} height={widthCalc.current?.offsetHeight} />;

  // Check if data contains daily aggregation type
  const supportsDailyAggregation = useMemo(() => {
    return (
      data?.metricsData?.some((m: any) =>
        m.data?.some((metric: any) =>
          metric.data?.some((point: any) => point.aggregationType?.toLowerCase() === "daily")
        )
      ) ?? false
    );
  }, [data]);
  const chartWidth = Math.max(width - 10, 0);

  const cardContent = useMemo(() => {
    // Get metrics data based on tenant flag
    const metricsData = data?.metricsData?.find((x: any) => x.isTenantMetrics === String(isTenantMetrics));

    if (!metricsData || !metricsData.data) return null;

    // If product supports daily aggregation and Daily is selected, use DailyChartWrapper
    if (supportsDailyAggregation && selectedAggregation.toLowerCase() === "daily") {
      // Get all metrics data (both tenant and non-tenant) for color index calculation
      const allData = data?.metricsData?.flatMap((m: any) => m.data) || [];
      return (
        <DailyChartWrapper metricsData={metricsData.data} allMetricsData={allData} width={chartWidth} height={400} />
      );
    }
    // Format chart data for line chart component
    const chartData = getMetricsData(data?.metricsData || [], isTenantMetrics);

    // Otherwise, use the standard monthly line chart
    return <LineChartComponent data={chartData} height={400} width={chartWidth} allowResize={true} />;
  }, [data, selectedAggregation, supportsDailyAggregation, isTenantMetrics, chartWidth]);

  const handleResize = useCallback(setWidth, []);

  useEffect(() => {
    if (widthCalc.current) {
      const _width = widthCalc.current?.offsetWidth;
      handleResize(_width);
    }
  }, [handleResize, resizedWidth]);

  const renderCard = () => {
    if (isLoading) return <CustomShimmer />;
    if (isError) return displayAPIsException;
    if (isSuccess && data) return cardContent;
    return null;
  };

  return (
    <div key={key} ref={widthCalc} style={PRODUCT_VIEW_STYLES.TAB.SUMMARY.cardContainer}>
      <CustomCard
        cardHeaderTitle={<>{typeof header === "string" ? header : header.title}</>}
        chartTitle={typeof header === "string" ? header : header.title}
        kustoLink={kustoLink}
      >
      {renderCard()}
      </CustomCard>
    </div>
  );
};
